import { useEffect, useRef, useState } from "react";
import { Button, Group, Stack } from "@mantine/core";
import { useDispatch } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";

import RuleColumnsTable from "../../../../components/RuleColumnsTable/RuleColumnsTable";
import { useRuleColumnsDataQuery } from "../../../../components/RuleColumnsTable/useRuleColumnsData";
import ExportFileButton from "../../../../components/ExportFileButton/ExportFileButton";
import ActivateReportModal from "../../../../components/Modals/ActivateReportModal";
import axios from "../../../../services/axios/config";
import { changeStatusSearch } from "../../../../services/flag";
import { getAxiosResponseError } from "../../../../utils/functions";
import {
  setFilterTablePage,
  setFilterTableTake,
  useSelectApplyNonce,
  useSelectFilterTablePage,
  useSelectFilterTableTake,
} from "../../../../redux/features/advancedSearch/advancedSearchSlice";

/**
 * Results table for the AdvancedSearch page. The grid fetches after
 * the user clicks Apply Filter (in SearchHeader) — applyNonce in the
 * slice gates the inner queries.
 */
export default function FilterResultsTable({ ruleId }) {
  const dispatch = useDispatch();
  const [exportLoading, setExportLoading] = useState(false);

  const applyNonce = useSelectApplyNonce();
  const page = useSelectFilterTablePage();
  const take = useSelectFilterTableTake();
  const enabled = applyNonce > 0;

  const containerRef = useRef(null);

  // Each "Apply filter" click bumps applyNonce; scroll the results
  // section (which lives at the bottom of the page, below the
  // QueryBuilder) into view so the user lands on the fresh results.
  useEffect(() => {
    if (applyNonce > 0) {
      containerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [applyNonce]);

  const { data, error } = useRuleColumnsDataQuery({
    ruleId,
    page,
    take,
    nonce: applyNonce,
    enabled,
  });
  const hasResults = enabled && !error && (data?.rows?.length ?? 0) > 0;

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const res = await axios({
        url: `search/advanced/export?ruleId=${ruleId}&type=EXCEL`,
        method: "POST",
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "file.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
      toast.error(getAxiosResponseError(err));
    } finally {
      setExportLoading(false);
    }
  };

  const sendForMonitoringMutation = useMutation({
    mutationFn: () => changeStatusSearch("FOR_MONITORING", ruleId),
    onSuccess: () => toast.success("Flag successfully added."),
    onError: (err) => toast.error(getAxiosResponseError(err)),
  });

  if (!enabled) {
    return null;
  }

  return (
    <Stack gap="sm" ref={containerRef}>
      <RuleColumnsTable
        ruleId={ruleId}
        page={page}
        take={take}
        onPageChange={(p) => dispatch(setFilterTablePage(p))}
        onTakeChange={(t) => dispatch(setFilterTableTake(t))}
        enabled={enabled}
        nonce={applyNonce}
      />

      {hasResults && (
        <Group justify="flex-end" gap="md" ml="sm" mb="sm" mr="sm">
          <ExportFileButton
            type="EXCEL"
            tooltipLabel="Export in excel"
            loading={exportLoading}
            handleDownload={handleExport}
          />
          <ActivateReportModal id={ruleId} />
          <Button
            variant="outline"
            onClick={() => sendForMonitoringMutation.mutate()}
            loading={sendForMonitoringMutation.isPending}
          >
            Send for monitoring
          </Button>
        </Group>
      )}
    </Stack>
  );
}
